import { useState, useEffect } from "react";
import AdminNav from "@/components/admin/AdminNav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, XCircle, Download, AlertTriangle } from "lucide-react";
import { seoMappings, validateSEOMapping } from "@/lib/seo-mapping";
import { redirects, validateRedirects } from "@/lib/seo-redirects";

const SEOValidation = () => {
  const [routes] = useState([
    "/",
    "/about-us",
    "/practice-areas",
    "/contact",
    "/blog",
    "/practice-areas/immigration-services",
    "/practice-areas/criminal-defense",
    "/practice-areas/personal-injury",
    "/practice-areas/family-law",
    "/practice-areas/tax-law",
    "/practice-areas/probate-estate",
    "/practice-areas/real-estate-law",
    "/practice-areas/business-law",
  ]);

  const [validationResults, setValidationResults] = useState<Array<{
    url: string;
    found: boolean;
    pageTitle?: string;
    metaDescription?: string;
    warning?: string;
  }>>([]);

  const [redirectIssues, setRedirectIssues] = useState<Array<{
    from: string;
    issue: string;
  }>>([]);

  useEffect(() => {
    // Validate all routes
    const results = routes.map(route => {
      const validation = validateSEOMapping(route);
      return {
        url: route,
        found: validation.found,
        pageTitle: validation.mapping?.pageTitle,
        metaDescription: validation.mapping?.metaDescription,
        warning: validation.warning
      };
    });
    setValidationResults(results);

    // Validate redirects
    const issues = validateRedirects();
    setRedirectIssues(issues);
  }, [routes]);

  const exportReport = () => {
    const csvContent = [
      ["URL", "Status", "Title", "Meta Description", "Issue"],
      ...validationResults.map(r => [
        r.url,
        r.found ? "mapped" : "missing",
        r.pageTitle || "",
        r.metaDescription || "",
        r.warning || ""
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `seo-validation-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const mappedCount = validationResults.filter(r => r.found).length;
  const missingCount = validationResults.filter(r => !r.found).length;
  const totalMappings = seoMappings.size;

  return (
    <div className="min-h-screen bg-background">
      <AdminNav />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">SEO Validation Dashboard</h1>
            <p className="text-muted-foreground">
              Control de calidad para URLs, títulos y meta descriptions
            </p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-6">
              <div className="text-sm text-muted-foreground mb-2">Total en CSV</div>
              <div className="text-3xl font-bold">{totalMappings}</div>
            </Card>
            
            <Card className="p-6">
              <div className="text-sm text-muted-foreground mb-2">URLs Mapeadas</div>
              <div className="text-3xl font-bold text-green-600">{mappedCount}</div>
            </Card>
            
            <Card className="p-6">
              <div className="text-sm text-muted-foreground mb-2">URLs Faltantes</div>
              <div className="text-3xl font-bold text-orange-600">{missingCount}</div>
            </Card>
            
            <Card className="p-6">
              <div className="text-sm text-muted-foreground mb-2">Redirects 301</div>
              <div className="text-3xl font-bold">{redirects.length}</div>
            </Card>
          </div>

          {/* Redirect Issues */}
          {redirectIssues.length > 0 && (
            <Alert className="mb-8 border-orange-500">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Problemas con Redirects:</strong>
                <ul className="mt-2 space-y-1">
                  {redirectIssues.map((issue, i) => (
                    <li key={i} className="text-sm">
                      {issue.from}: {issue.issue}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Export Button */}
          <div className="mb-6 flex justify-end">
            <Button onClick={exportReport} className="gap-2">
              <Download className="h-4 w-4" />
              Exportar Reporte CSV
            </Button>
          </div>

          {/* Validation Results Table */}
          <Card>
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Resultados de Validación</h2>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Title Tag</TableHead>
                    <TableHead>Meta Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validationResults.map((result, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-sm">{result.url}</TableCell>
                      <TableCell>
                        {result.found ? (
                          <Badge className="gap-1 bg-green-600">
                            <CheckCircle2 className="h-3 w-3" />
                            Mapeada
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="gap-1">
                            <XCircle className="h-3 w-3" />
                            Faltante
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm">
                        {result.pageTitle || <span className="text-muted-foreground italic">No definido</span>}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm">
                        {result.metaDescription || <span className="text-muted-foreground italic">No definido</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Instructions */}
          <Card className="mt-8 p-6 bg-muted/50">
            <h3 className="text-lg font-semibold mb-3">📋 Instrucciones de Uso</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>✅ <strong>URLs Mapeadas:</strong> Tienen título y meta description del CSV</li>
              <li>⚠️ <strong>URLs Faltantes:</strong> Necesitan agregarse al CSV de referencia</li>
              <li>🔄 <strong>Redirects:</strong> Se validan automáticamente para evitar cadenas</li>
              <li>💾 <strong>Exportar:</strong> Genera reporte CSV para revisión completa</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SEOValidation;
